import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useLocation, useParams } from 'react-router-dom';
import { ReactSVG } from 'react-svg';

import ChevronDown from '@navozyme/uikit/dist/atoms/Icons/ChevronDown';
import ChevronUp from '@navozyme/uikit/dist/atoms/Icons/ChevronUp';

import {
  DossierSectionOptions,
  DossierSectionStatusOptions,
  SectionName,
  SectionNameMapper,
} from 'features/captains-form/slices/constants';

import {
  selectPassengers,
  StatusLog,
} from '../../captains-form/slices/portCallSlice';

import { FormRefContext } from '../../global/context/FormRefContext';

import RoundedGreenCheckIcon from '../styles/images/rounded-check-icon-green.svg';

import '../styles/sidebar.scss';

const setClassName = ({
  isActive,
  isPending,
}: {
  isActive: boolean;
  isPending: boolean;
}) => (isActive ? 'active' : isPending ? 'pending' : '');

export type SidebarMenuItem = {
  title: string;
  key?: DossierSectionOptions;
  navLink?: string;
  children?: SidebarMenuItem[];
};

export const showPassengersSection = (
  arrivalPassengers: number | null | undefined,
  departurePassengers: number | null | undefined
): boolean => {
  if (arrivalPassengers != null && departurePassengers != null) {
    return arrivalPassengers > 0 || departurePassengers > 0;
  }
  return false;
};

type SidebarProps = {
  portallStatusLog: StatusLog[];
};

const Sidebar = ({ portallStatusLog }: SidebarProps) => {
  const { formContextRef, multiFormRef, skipOrBack, setSkipOrBack } =
    useContext(FormRefContext);
  const location = useLocation();
  const params = useParams();
  const initialRender = useRef(true);
  const previousLocation = useRef(location);
  const { arrival: arrivalPassengers, departure: departurePassengers } =
    useSelector(selectPassengers);

  const cargoShipMenuItems: SidebarMenuItem[] = useMemo(
    () => [
      {
        title: 'General Declaration',
        children: [
          {
            title: 'Port call data',
            navLink: '/port-call-data',
          },
          {
            title: 'Vessel Data',
            navLink: '/vessel-data',
          },
          {
            title: 'Inspections',
            navLink: '/inspections',
          },
          {
            title: 'Cargo Information',
            navLink: '/cargo',
          },
          {
            title: 'Dangerous Goods',
            navLink: '/dangerous-goods',
          },
          {
            title: 'Declaration',
            navLink: '/general-declaration',
          },
        ],
      },
      {
        title: 'SOLAS and ISPS code',
        navLink: '/solas-and-isps-code',
      },
      {
        title: 'Crew',
        children: [
          {
            title: 'Arrival',
            navLink: '/arrival-crew',
            key: DossierSectionOptions.ArrivalCrew,
          },
          {
            title: 'Departure',
            navLink: '/departure-crew',
            key: DossierSectionOptions.DepartureCrew,
          },
        ],
      },
      {
        title: 'Marpol waste',
        navLink: '/marpol',
      },
      {
        title: 'Health Declaration',
        navLink: '/health',
      },
      {
        title: 'Local Formalities',
        navLink: '/local-formalities',
      },
      {
        title: 'Ship Certificates',
        navLink: '/ship-certificates',
      },
      {
        title: 'Summary',
        navLink: '/summary',
      },
    ],
    []
  );

  const [expandedItems, setExpandedItems] = useState<{
    [key: string]: boolean;
  }>({});

  const sidebarMenuItems = useMemo(() => {
    const count = showPassengersSection(
      arrivalPassengers?.passengerQuantity,
      departurePassengers?.passengerQuantity
    );
    if (count) {
      const menuItems = [...cargoShipMenuItems];
      const passengerMenuItem: SidebarMenuItem = {
        title: 'Passengers',
        children: [
          {
            title: 'Arrival',
            navLink: '/arrival-passengers',
            key: DossierSectionOptions.ArrivalPassenger,
          },
          {
            title: 'Departure',
            navLink: '/departure-passengers',
            key: DossierSectionOptions.DeparturePassenger,
          },
        ],
      };
      menuItems.splice(3, 0, passengerMenuItem);
      return menuItems;
    } else {
      return cargoShipMenuItems;
    }
  }, [
    arrivalPassengers?.passengerQuantity,
    cargoShipMenuItems,
    departurePassengers?.passengerQuantity,
  ]);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
    } else if (previousLocation.current.pathname !== location.pathname) {
      if (formContextRef) formContextRef.current = null;
      if (multiFormRef) multiFormRef.current = null;
      setSkipOrBack(false);
    }
    previousLocation.current = location;
  }, [formContextRef, location, multiFormRef, setSkipOrBack, skipOrBack]);

  // Expand the parent section if the current route matches a child navLink
  useEffect(() => {
    sidebarMenuItems.forEach((item, index) => {
      if (item.children) {
        const childMatch = item.children.find((child) =>
          location.pathname.includes(child.navLink!)
        );
        if (childMatch) {
          setExpandedItems((prevState) => ({
            ...prevState,
            [index]: true,
          }));
        }
      }
    });
  }, [location.pathname, sidebarMenuItems]);

  const toggleExpand = (index: number) => {
    setExpandedItems((prevState) => ({
      ...prevState,
      [index]: !prevState[index],
    }));
  };

  const isSubmitted = (item: SidebarMenuItem) => {
    if (item.title === 'Summary') return false;

    if (portallStatusLog && portallStatusLog.length > 0) {
      const sectionNames = SectionNameMapper.find(
        (sec: SectionName) =>
          sec.sectionDisplayName === item.title &&
          (item.key !== undefined ? item.key === sec.sectionName[0] : true)
      )?.sectionName;
      let isSubmitted = true;
      if (sectionNames) {
        for (const sectionName of sectionNames) {
          const statusLog = portallStatusLog?.find(
            (log) => log.section === sectionName
          );
          isSubmitted =
            isSubmitted &&
            statusLog !== undefined &&
            statusLog?.status !==
              DossierSectionStatusOptions.NotSubmittedToNMap;
        }
      }
      return isSubmitted;
    }
  };

  if (
    !location.pathname.includes('/welcome') &&
    !location.pathname.includes('/port-clearance-form-request')
  ) {
    return (
      <aside className="sidebar">
        <h4>Port clearance form</h4>
        <ul>
          {sidebarMenuItems.map((item, index) => {
            const isExpanded = expandedItems[index];
            if (item.children) {
              return (
                <li key={index} className="sidebar__multiple-items">
                  <div
                    className={`sidebar__multiple-items-parent ${isSubmitted(item) ? 'submitted' : ''}`}
                    onClick={() => toggleExpand(index)}>
                    {isSubmitted(item) && (
                      <ReactSVG
                        className="green-tick"
                        src={RoundedGreenCheckIcon}
                      />
                    )}
                    <span>
                      {index + 1}.{item.title}
                    </span>
                    <span className="sidebar__icon">
                      {isExpanded ? <ChevronUp /> : <ChevronDown />}
                    </span>
                  </div>
                  {isExpanded ? (
                    <ul className="sidebar__multiple-items-children">
                      {item.children.map((child, childIndex) => {
                        return (
                          <li key={childIndex}>
                            <NavLink
                              to={`/${params.uid}${child.navLink}`}
                              className={`${setClassName} ${isSubmitted(child) ? 'submitted' : ''}`}>
                              {isSubmitted(child) && (
                                <ReactSVG
                                  className="green-tick"
                                  src={RoundedGreenCheckIcon}
                                />
                              )}
                              {child.title}
                            </NavLink>
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                </li>
              );
            }
            return (
              <li key={index} className="sidebar__single-item">
                <NavLink
                  to={`/${params.uid}${item.navLink}`}
                  className={`${setClassName} ${isSubmitted(item) ? 'submitted' : ''}`}>
                  {isSubmitted(item) && (
                    <ReactSVG
                      className="green-tick"
                      src={RoundedGreenCheckIcon}
                    />
                  )}
                  {index + 1}.{item.title}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </aside>
    );
  }

  return null;
};

export default Sidebar;
